import { useEffect, useMemo, useRef, useState } from "react";
import AppLayout from "../layouts/AppLayout.jsx";
import API from "../config/api.js";
import { useAuth } from "../context/AuthContext.jsx";

const DEFAULT_DAYS = {
  0: 0,
  1: 0,
  2: 0,
  3: 0,
  4: 0,
  5: 0,
  6: 0,
};

const DAY_LABELS = [
  { key: 0, label: "Su" },
  { key: 1, label: "Mo" },
  { key: 2, label: "Tu" },
  { key: 3, label: "We" },
  { key: 4, label: "Th" },
  { key: 5, label: "Fr" },
  { key: 6, label: "Sa" },
];

function getBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

function getTimezoneOptions() {
  try {
    if (typeof Intl.supportedValuesOf === "function") {
      return Intl.supportedValuesOf("timeZone").map((tz) => {
        const now = new Date();
        const parts = new Intl.DateTimeFormat("en-US", {
          timeZone: tz,
          timeZoneName: "shortOffset",
        }).formatToParts(now);

        const tzName =
          parts.find((part) => part.type === "timeZoneName")?.value || "GMT";

        return {
          value: tz,
          label: `(${tzName}) ${tz}`,
        };
      });
    }
  } catch (err) {
    console.error("Erro ao gerar timezones:", err);
  }

  const fallback = getBrowserTimezone();

  return [
    { value: fallback, label: fallback },
    { value: "UTC", label: "UTC" },
  ];
}

function parseBrands(brands) {
  if (!brands) return [];
  if (Array.isArray(brands)) return brands.map(String);

  return String(brands)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function parseDaystopost(daystopost) {
  if (typeof daystopost === "object" && daystopost !== null) {
    return { ...DEFAULT_DAYS, ...daystopost };
  }

  try {
    const parsed = JSON.parse(daystopost || "{}");
    return { ...DEFAULT_DAYS, ...parsed };
  } catch {
    return { ...DEFAULT_DAYS };
  }
}

function buildFormFromAuth(authData) {
  const user = authData?.user || {};

  const userCategories = Array.isArray(authData?.user_categories)
    ? authData.user_categories
    : Array.isArray(authData?.userCategories)
    ? authData.userCategories
    : [];

  const nicheCategories = userCategories
    .filter((item) => item.type === "doneforyou")
    .map((item) => String(item.idcategory));

  const destinationCategories = userCategories
    .filter((item) => item.type === "destination")
    .map((item) => String(item.idcategory));

  return {
    personalizedcontent:
      Number(user.personalizedcontent) === 1 ||
      user.personalizedcontent === true,
    website: user.website || "",
    name: user.name || "",
    businessname: user.businessname || "",
    teamsize: Number(user.teamsize) > 0 ? Number(user.teamsize) : 1,
    nicheCategories,
    destinationCategories,
    brands: parseBrands(user.brands),
    clientdescription: user.clientdescription || "",
    kindoftraveler: user.kindoftraveler || "",
    whyyoulovewhatyoudo: user.whyyoulovewhatyoudo || "",
    relatedfacts: user.relatedfacts || "",
    proundof: user.proundof || "",
    timezonelocation: user.timezonelocation || getBrowserTimezone(),
    daystopost: parseDaystopost(user.daystopost),
    timetopost: user.timetopost || "13:20",
  };
}

export default function Preferences() {
  // refreshAuth adicionado: atualiza o contexto global após o save,
  // garantindo que ao sair e voltar para a página os dados já estejam corretos.
  const { user, userCategories, loading, refreshAuth } = useAuth();

  const authData = useMemo(
    () => ({
      user,
      user_categories: userCategories,
    }),
    [user, userCategories]
  );

  const timezoneOptions = useMemo(() => getTimezoneOptions(), []);

  const nicheSelectRef = useRef(null);
  const destinationSelectRef = useRef(null);
  const brandsInputRef = useRef(null);

  const nicheChoicesRef = useRef(null);
  const destinationChoicesRef = useRef(null);
  const brandsChoicesRef = useRef(null);

  const syncingNicheRef = useRef(false);
  const syncingDestinationRef = useRef(false);
  const syncingBrandsRef = useRef(false);

  const [categories, setCategories] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [form, setForm] = useState(() => buildFormFromAuth(authData));

  const nicheOptions = useMemo(() => {
    return categories.filter((item) => item.type === "doneforyou");
  }, [categories]);

  const destinationOptions = useMemo(() => {
    return categories.filter((item) => item.type === "destination");
  }, [categories]);

  const updateField = (name, value) => {
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
    setHasChanges(true);
  };

  function handleNicheChange() {
    if (syncingNicheRef.current) return;

    const values = nicheChoicesRef.current?.getValue(true);
    const normalized = Array.isArray(values)
      ? values.map((item) => String(item))
      : values
      ? [String(values)]
      : [];

    setForm((prev) => ({
      ...prev,
      nicheCategories: normalized,
    }));
    setHasChanges(true);
  }

  function handleDestinationChange() {
    if (syncingDestinationRef.current) return;

    const values = destinationChoicesRef.current?.getValue(true);
    const normalized = Array.isArray(values)
      ? values.map((item) => String(item))
      : values
      ? [String(values)]
      : [];

    setForm((prev) => ({
      ...prev,
      destinationCategories: normalized,
    }));
    setHasChanges(true);
  }

  function syncBrandsFromChoices() {
    if (syncingBrandsRef.current) return;

    const values = brandsChoicesRef.current?.getValue(true);
    const normalized = Array.isArray(values)
      ? values.map((item) => String(item).trim()).filter(Boolean)
      : values
      ? [String(values).trim()]
      : [];

    setForm((prev) => {
      const same =
        prev.brands.length === normalized.length &&
        prev.brands.every((item, index) => item === normalized[index]);

      if (same) return prev;

      return {
        ...prev,
        brands: normalized,
      };
    });

    setHasChanges(true);
  }

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API.categories}?list`, {
        credentials: "include",
      });

      const data = await res.json();
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error", err);
      setCategories([]);
    }
  };

  const fetchPreferences = async () => {
    try {
      const res = await fetch(`${API.preferences}?get`, {
        credentials: "include",
      });

      const data = await res.json();

      setForm(buildFormFromAuth(data));
      setHasChanges(false);
    } catch (err) {
      console.error("Error", err);

      if (user) {
        setForm(buildFormFromAuth(authData));
        setHasChanges(false);
      }
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (!loading) {
      setForm(buildFormFromAuth(authData));
      setHasChanges(false);
    }
  }, [authData, loading]);

  useEffect(() => {
    if (!window.Choices) return;

    const nicheElement = nicheSelectRef.current;
    const destinationElement = destinationSelectRef.current;

    if (!nicheElement || !destinationElement) return;

    if (!nicheChoicesRef.current) {
      nicheChoicesRef.current = new window.Choices(nicheElement, {
        searchEnabled: true,
        searchChoices: true,
        searchPlaceholderValue: "Search",
        placeholder: true,
        removeItemButton: true,
        maxItemCount: 3,
        shouldSort: false,
        itemSelectText: "",
      });

      nicheElement.addEventListener("change", handleNicheChange);
    }

    if (!destinationChoicesRef.current) {
      destinationChoicesRef.current = new window.Choices(destinationElement, {
        searchEnabled: true,
        searchChoices: true,
        searchPlaceholderValue: "Search",
        placeholder: true,
        removeItemButton: true,
        maxItemCount: 3,
        shouldSort: false,
        itemSelectText: "",
      });

      destinationElement.addEventListener("change", handleDestinationChange);
    }

    return () => {
      nicheElement.removeEventListener("change", handleNicheChange);
      destinationElement.removeEventListener("change", handleDestinationChange);

      nicheChoicesRef.current?.destroy();
      destinationChoicesRef.current?.destroy();

      nicheChoicesRef.current = null;
      destinationChoicesRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!window.Choices) return;
    if (loading) return;

    const brandsElement = brandsInputRef.current;
    if (!brandsElement) return;

    if (brandsChoicesRef.current) {
      brandsElement.removeEventListener("addItem", syncBrandsFromChoices);
      brandsElement.removeEventListener("removeItem", syncBrandsFromChoices);
      brandsElement.removeEventListener("change", syncBrandsFromChoices);

      brandsChoicesRef.current.destroy();
      brandsChoicesRef.current = null;
    }

    syncingBrandsRef.current = true;

    brandsElement.value = Array.isArray(form.brands)
      ? form.brands.join(",")
      : "";

    brandsChoicesRef.current = new window.Choices(brandsElement, {
      removeItemButton: true,
      duplicateItemsAllowed: false,
      maxItemCount: 5,
      shouldSort: false,
      itemSelectText: "",
      addItems: true,
      editItems: true,
      delimiter: ",",
      paste: true,
      placeholder: true,
      placeholderValue: "Choose your option",
      addItemFilter: (value) => String(value).trim().length > 0,
    });

    brandsElement.addEventListener("addItem", syncBrandsFromChoices);
    brandsElement.addEventListener("removeItem", syncBrandsFromChoices);
    brandsElement.addEventListener("change", syncBrandsFromChoices);

    requestAnimationFrame(() => {
      syncingBrandsRef.current = false;
    });

    return () => {
      brandsElement.removeEventListener("addItem", syncBrandsFromChoices);
      brandsElement.removeEventListener("removeItem", syncBrandsFromChoices);
      brandsElement.removeEventListener("change", syncBrandsFromChoices);

      brandsChoicesRef.current?.destroy();
      brandsChoicesRef.current = null;
    };
  }, [loading, form.brands]);

  useEffect(() => {
    if (!nicheChoicesRef.current) return;
    if (!nicheOptions.length) return;

    syncingNicheRef.current = true;

    nicheChoicesRef.current.clearChoices();
    nicheChoicesRef.current.setChoices(
      nicheOptions.map((item) => ({
        value: String(item.idcategory),
        label: item.title,
        selected: false,
      })),
      "value",
      "label",
      true
    );

    nicheChoicesRef.current.removeActiveItems();

    form.nicheCategories.forEach((value) => {
      nicheChoicesRef.current.setChoiceByValue(String(value));
    });

    requestAnimationFrame(() => {
      syncingNicheRef.current = false;
    });
  }, [nicheOptions, form.nicheCategories]);

  useEffect(() => {
    if (!destinationChoicesRef.current) return;
    if (!destinationOptions.length) return;

    syncingDestinationRef.current = true;

    destinationChoicesRef.current.clearChoices();
    destinationChoicesRef.current.setChoices(
      destinationOptions.map((item) => ({
        value: String(item.idcategory),
        label: item.title,
        selected: false,
      })),
      "value",
      "label",
      true
    );

    destinationChoicesRef.current.removeActiveItems();

    form.destinationCategories.forEach((value) => {
      destinationChoicesRef.current.setChoiceByValue(String(value));
    });

    requestAnimationFrame(() => {
      syncingDestinationRef.current = false;
    });
  }, [destinationOptions, form.destinationCategories]);

  const toggleDay = (day) => {
    setForm((prev) => ({
      ...prev,
      daystopost: {
        ...prev.daystopost,
        [day]: prev.daystopost[day] === 1 ? 0 : 1,
      },
    }));
    setHasChanges(true);
  };

  const handleCancel = async () => {
    if (user) {
      setForm(buildFormFromAuth(authData));
      setHasChanges(false);
      return;
    }

    await fetchPreferences();
  };

  const handleSave = async () => {
    try {
      const formData = new FormData();

      formData.append(
        "personalizedcontent",
        form.personalizedcontent ? "1" : "0"
      );
      formData.append("website", form.website);
      formData.append("name", form.name);
      formData.append("businessname", form.businessname);
      formData.append("teamsize", String(form.teamsize));
      formData.append("clientdescription", form.clientdescription);
      formData.append("timezonelocation", form.timezonelocation);
      formData.append("brands", form.brands.join(","));
      formData.append("daystopost", JSON.stringify(form.daystopost));
      formData.append("timetopost", form.timetopost);
      formData.append("kindoftraveler", form.kindoftraveler);
      formData.append("whyyoulovewhatyoudo", form.whyyoulovewhatyoudo);
      formData.append("relatedfacts", form.relatedfacts);
      formData.append("proundof", form.proundof);

      [...form.nicheCategories, ...form.destinationCategories].forEach((id) => {
        formData.append("idcategory[]", id);
      });

      const res = await fetch(`${API.account}?savePreferences`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(data?.message || "Erro ao salvar");
      }

      // Atualiza o AuthContext com os dados frescos do servidor.
      // Isso garante que ao navegar para outra página e voltar,
      // o useEffect([authData]) já encontre os dados corretos
      // e não sobrescreva o form com a versão antiga.
      await refreshAuth();

      setHasChanges(false);

      if (window._alert) {
        window._alert("success", "Saved");
      } else {
        alert("Saved");
      }
    } catch (err) {
      console.error("Erro ao salvar preferências:", err);

      if (window._alert) {
        window._alert("error", "Error saving preferences");
      } else {
        alert("Error saving preferences");
      }
    }
  };

  return (
    <AppLayout currentPage="preferences">
      <form id="onboarding">
        <div className="row mb-4 align-items-end">
          <div className="col-md-7">
            <h2 className="fs-4 fw-700">
              Welcome! Let&apos;s get you marketing like a pro.
              <br />
              The right content starts with the right setup.
            </h2>

            <p className="mb-0">
              Help us understand your business so we can personalize your
              experience. Answer a few quick questions, and we'll tailor your
              content suggestions, calendar, and captions to fit your niche and
              audience.
            </p>
          </div>

          <div
            className={`col-md-5 text-end ${hasChanges ? "" : "d-none"}`}
            id="save-buttons"
          >
            <button
              type="button"
              onClick={handleCancel}
              className="btn btn-secondary me-2"
            >
              Cancel Changes
            </button>

            <button
              type="button"
              onClick={handleSave}
              className="btn btn-primary"
            >
              Save Changes <i className="fi fi-rr-check ms-2"></i>
            </button>
          </div>
        </div>

        <section>
          <div className="row">
            <div className="col-md-12">
              <div className="border rounded-3 p-4 h-100">
                <div className="p-2">
                  <h5 className="fw-700">Consent to Personalize Content</h5>

                  <p className="text-muted">
                    To make your content truly yours, we&apos;ll scan your
                    public website and social profiles for inspiration —
                    focusing only on your voice, story, and style.
                    <br />
                    We&apos;ll never use your data for ads, spam, or anything
                    outside of crafting your personalized content.
                  </p>

                  <div className="border rounded-3 p-3">
                    <div className="form-check fs-6">
                      <input
                        className="form-check-input rounded-circle fs-6"
                        type="checkbox"
                        id="personalizedcontent"
                        checked={form.personalizedcontent}
                        onChange={(e) =>
                          updateField("personalizedcontent", e.target.checked)
                        }
                      />

                      <label
                        className="form-check-label fw-700 fs-6 w-100"
                        htmlFor="personalizedcontent"
                      >
                        I&apos;m happy to let you personalize my content this
                        way!
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mt-4">
          <div className="row g-4 align-items-stretch">
            <div className="col-md-12">
              <div className="border rounded-3 p-4 h-100">
                <div className="p-2">
                  <h5 className="fw-700">About Your Business</h5>
                  <p>
                    Tell us a bit about yourself and your business so we can
                    offer a personalized experience and solutions.
                  </p>

                  <div className="row g-4">
                    <div className="col-md-6">
                      <label className="fw-bold mb-2">Your Website URL</label>
                      <input
                        type="text"
                        className="form-control form-control-lg"
                        placeholder="Paste the link to your main website"
                        value={form.website}
                        onChange={(e) => updateField("website", e.target.value)}
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="fw-bold mb-2">Name</label>
                      <input
                        type="text"
                        className="form-control form-control-lg"
                        placeholder="Name as you'd like it to appear"
                        value={form.name}
                        onChange={(e) => updateField("name", e.target.value)}
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="fw-bold mb-2">Business Name</label>
                      <input
                        type="text"
                        className="form-control form-control-lg"
                        placeholder="Name that is associated with your brand"
                        value={form.businessname}
                        onChange={(e) =>
                          updateField("businessname", e.target.value)
                        }
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="fw-bold mb-2">Team Size</label>
                      <input
                        type="number"
                        min="1"
                        className="form-control form-control-lg"
                        value={form.teamsize}
                        onChange={(e) =>
                          updateField(
                            "teamsize",
                            Number(e.target.value) > 0
                              ? Number(e.target.value)
                              : 1
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="border rounded-3 p-4 h-100">
                <div className="p-2">
                  <h5 className="fw-700">
                    What kind of travel do you specialize in?
                  </h5>
                  <p className="mb-4">
                    Think about who you serve and the types of travel you book.
                  </p>

                  <label className="fw-bold mb-2">Niche</label>
                  <select
                    ref={nicheSelectRef}
                    multiple
                    className="form-select select-choices-multiple"
                  />
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="border rounded-3 p-4 h-100">
                <div className="p-2">
                  <h5 className="fw-700">
                    What destinations do you specialize in booking for clients?
                  </h5>
                  <p className="mb-4">
                    These are the places you book most or want to book more of.
                  </p>

                  <label className="fw-bold mb-2">Top Destinations</label>
                  <select
                    ref={destinationSelectRef}
                    multiple
                    className="form-select select-choices-multiple"
                  />
                </div>
              </div>
            </div>

            <div className="col-md-12">
              <div className="border rounded-3 p-4 h-100">
                <div className="p-2">
                  <h5 className="fw-700">
                    What are the top travel brands you book?
                  </h5>
                  <p className="mb-4">
                    Share hotel groups, cruise lines and tour operators you
                    actively book.
                  </p>

                  <label className="fw-bold mb-2">Favorite Brands</label>
                  <input
                    ref={brandsInputRef}
                    type="text"
                    name="brands"
                    className="form-select text-input-choices"
                    defaultValue=""
                  />
                </div>
              </div>
            </div>

            <div className="col-md-12">
              <div className="border rounded-3 p-4 h-100">
                <div className="p-2 mb-2">
                  <h5 className="fw-700">
                    Tell us everything you know about your ideal client
                  </h5>
                  <p className="mb-4">
                    Who do you get most excited to plan for? Speak to their
                    lifestyle, priorities, and what lights them up.
                  </p>

                  <label className="fw-bold mb-2">Dream Client Avatar</label>
                  <textarea
                    rows="3"
                    className="form-control form-control-lg"
                    placeholder="Professional couples looking for meaningful trips with a touch of luxury. Their priorities are cultural immersion, food, beautiful hotels and easy planning."
                    value={form.clientdescription}
                    onChange={(e) =>
                      updateField("clientdescription", e.target.value)
                    }
                  />
                </div>

                <div className="row g-4">
                  <div className="col-md-6">
                    <label className="fw-bold mb-2">
                      What kind of traveler are you outside of work?
                    </label>
                    <textarea
                      rows="3"
                      className="form-control form-control-lg"
                      value={form.kindoftraveler}
                      onChange={(e) =>
                        updateField("kindoftraveler", e.target.value)
                      }
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="fw-bold mb-2">
                      What's one story or client moment you always tell when
                      people ask why you love what you do?
                    </label>
                    <textarea
                      rows="3"
                      className="form-control form-control-lg"
                      value={form.whyyoulovewhatyoudo}
                      onChange={(e) =>
                        updateField("whyyoulovewhatyoudo", e.target.value)
                      }
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="fw-bold mb-2">
                      What are 3 fun travel related facts about you?
                    </label>
                    <textarea
                      rows="3"
                      className="form-control form-control-lg"
                      value={form.relatedfacts}
                      onChange={(e) =>
                        updateField("relatedfacts", e.target.value)
                      }
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="fw-bold mb-2">
                      What's one win or client story you're proud of?
                    </label>
                    <textarea
                      rows="3"
                      className="form-control form-control-lg"
                      value={form.proundof}
                      onChange={(e) => updateField("proundof", e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-12">
              <div className="border rounded-3 p-4 h-100">
                <div className="p-2">
                  <h5 className="fw-700">Choose your posting schedule</h5>
                  <p className="mb-4">
                    Ready To Set Up Your Social Media Scheduler?
                  </p>

                  <div className="row">
                    <div className="col-md-5">
                      <label className="fw-bold mb-1">
                        What&apos;s your preferred time zone for scheduling?
                      </label>

                      <select
                        className="form-select form-select-lg"
                        value={form.timezonelocation}
                        onChange={(e) =>
                          updateField("timezonelocation", e.target.value)
                        }
                      >
                        {timezoneOptions.map((timezone) => (
                          <option key={timezone.value} value={timezone.value}>
                            {timezone.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-7">
                      <label className="fw-bold mb-1">
                        Choose your posting schedule
                      </label>

                      <div className="row g-2" id="week-buttons">
                        {DAY_LABELS.map((day) => (
                          <div className="col" key={day.key}>
                            <button
                              type="button"
                              className={`btn btn-outline-primary w-100 ${
                                Number(form.daystopost[day.key]) === 1
                                  ? "active"
                                  : ""
                              }`}
                              onClick={() => toggleDay(day.key)}
                            >
                              {day.label}
                            </button>
                          </div>
                        ))}

                        <div className="col">
                          <input
                            type="time"
                            className="form-control form-control-lg w-100"
                            value={form.timetopost}
                            onChange={(e) =>
                              updateField("timetopost", e.target.value)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </form>
    </AppLayout>
  );
}